import React from "react";
import { MRT_ColumnDef } from "material-react-table";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import { EditableSelectCell } from "../EditableSelectCell";
import DataSheetCell from "./DataSheetCell";
import { getUnitForComponent } from "./utils";

export const booleanOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const FONT_SIZE = "0.65rem";

/* ----------------------------- Hook-safe cells ----------------------------- */

// function matchUidByLineNumber(lineNumber, uidRows) {
//   if (!lineNumber || !Array.isArray(uidRows)) return "";
//   const line = String(lineNumber).trim();
//   const found = uidRows.find((r) => {
//     if (r.uid) return r.uid === line;
//     const t = String(r.tag ?? "").trim();
//     const n = String(r.registerNumber ?? "").trim();
//     if (t && n) return `${t}-${n}` === line;
//     return (t || n) === line;
//   });
//   if (!found) return "";
//   if (found.uid) return found.uid;
//   const t = String(found.tag ?? "").trim();
//   const n = String(found.registerNumber ?? "").trim();
//   if (t && n) return `${t}-${n}`;
//   return t || n;
// }
function matchUidByMaterialDescription(matDesc, uidRows) {
  if (!matDesc || !Array.isArray(uidRows)) return "";
  const desc = String(matDesc).trim().toLowerCase();
  const found = uidRows.find((r) => {
    const rDesc = String(
      r?.["Material Description"] ?? r?.materialDescription ?? r?.matDesc ?? ""
    ).trim().toLowerCase();
    return rDesc && rDesc === desc;
  });
  if (!found) return "";
  if (found.uid) return found.uid;
  const t = String(found.tag ?? "").trim();
  const n = String(found.registerNumber ?? "").trim();
  if (t && n) return `${t}-${n}`;
  return t || n;
}


function TextCommitCell({ value, fontSize = FONT_SIZE, onCommit }) {
  const [v, setV] = React.useState(value ?? "");

  React.useEffect(() => {
    setV(value ?? "");
  }, [value]);

  return (
    <TextField
      variant="standard"
      size="small"
      fullWidth
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      inputProps={{ style: { fontSize } }}
    />
  );
}

function NumberCommitCell({
  value,
  placeholder,
  fontSize = FONT_SIZE,
  onCommit,
}) {
  const [v, setV] = React.useState(value ?? "");

  React.useEffect(() => {
    setV(value ?? "");
  }, [value]);

  return (
    <TextField
      type="number"
      size="small"
      placeholder={placeholder}
      variant="standard"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      fullWidth
      inputProps={{ style: { fontSize } }}
    />
  );
}

function SelectCommitCell({ value, options, disabled, onCommit }) {
  return (
    <EditableSelectCell
      value={value ?? ""}
      options={options}
      disabled={disabled}
      onChange={onCommit}
    />
  );
}

function DeleteRowCell({ onDelete }) {
  return (
    <IconButton color="error" onClick={onDelete} aria-label="delete">
      <DeleteIcon />
    </IconButton>
  );
}

/* ----------------------------- Helpers ----------------------------- */

const inputCellBg = (matchLevel, field) => {
  if (!matchLevel) return "transparent";
  const contributed = {
    Size: ["exact", "sch", "size"],
    "Schedule/Rating": ["exact", "sch"],
    Ends: ["exact"],
  };
  return contributed[field]?.includes(matchLevel)
    ? "rgba(76, 175, 80, 0.30)"
    : "transparent";
};

/* ------------------------- Spec Library cascade cells ------------------------- */
// Rows are added manually. Spec/Components/Size/Schedule-Rating/Ends/Material
// are picked in order via cascading dropdowns sourced from the Spec Library
// (specLibraryTable in PipingContext) — each choice narrows the options for the
// next field. Every other column (Material Description, Family Table, Unit, …)
// is filled in once the chain resolves to a matching Spec Library row.

const CASCADE_FIELDS = [
  "Spec",
  "Components",
  "Size",
  "Schedule/Rating",
  "Ends",
  "Material",
];

// Spec Library bookkeeping that must not be copied onto an estimation row.
const NON_COPYABLE_LIB_FIELDS = new Set([
  ...CASCADE_FIELDS,
  "select",
  "TabName",
  "UID",
  "Line",
  "Line Number",
  "TagNumber",
  "Quantity",
  "Qty",
  "MatchLevel",
]);

// specLibraryTable is already a flat array of expanded rows; keep only the rows
// whose CategoryType belongs to this table ("Bulk" vs Valve/Instrument).
function buildLibRows(specLibraryTable, isBulkTab) {
  const rows = Array.isArray(specLibraryTable) ? specLibraryTable : [];
  return rows.filter((r) =>
    isBulkTab
      ? r.CategoryType === "Bulk"
      : r.CategoryType && r.CategoryType !== "Bulk",
  );
}

function getCascadeOptions(libRows, rowValues, field) {
  const idx = CASCADE_FIELDS.indexOf(field);
  const priorFields = CASCADE_FIELDS.slice(0, idx);

  // Every earlier field in the chain must already be picked — a field has no
  // options of its own until the ones before it are resolved (Spec first,
  // then Components, then Size, ...).
  if (priorFields.some((f) => !rowValues[f])) return [];

  const filtered = libRows.filter((r) =>
    priorFields.every((f) => r[f] === rowValues[f]),
  );
  const vals = filtered
    .map((r) => r[field])
    .filter((v) => v !== undefined && v !== null && v !== "");
  return [...new Set(vals)].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    }),
  );
}

function findMatchingLibRow(libRows, rowValues) {
  return libRows.find((r) =>
    CASCADE_FIELDS.every((f) => (rowValues[f] ?? "") === (r[f] ?? "")),
  );
}

function CascadeSelectCell({ row, field, libRows, handleFieldUpdate, isMetric }) {
  const rowValues = {
    Spec: row.original.Spec ?? "",
    Components: row.original.Components ?? "",
    Size: row.original.Size ?? "",
    "Schedule/Rating": row.original["Schedule/Rating"] ?? "",
    Ends: row.original.Ends ?? "",
    Material: row.original.Material ?? "",
  };

  const options = getCascadeOptions(libRows, rowValues, field);

  const handleChange = (value) => {
    handleFieldUpdate(row.index, field, value);

    // Clear every downstream cascade field — a change upstream invalidates them
    const idx = CASCADE_FIELDS.indexOf(field);
    for (let i = idx + 1; i < CASCADE_FIELDS.length; i++) {
      handleFieldUpdate(row.index, CASCADE_FIELDS[i], "");
    }
    handleFieldUpdate(row.index, "Material Description", "");

    if (field === "Components") {
      handleFieldUpdate(row.index, "Unit", getUnitForComponent(value, isMetric));
    }

    // Once the whole chain is resolved, copy every remaining column off the
    // matching Spec Library row (Material Description, Family Table, Stock
    // Code, Additional Notes, CategoryType, …).
    const nextValues = { ...rowValues, [field]: value };
    const isChainComplete = CASCADE_FIELDS.every((f) => nextValues[f]);
    if (!isChainComplete) return;

    const match = findMatchingLibRow(libRows, nextValues);
    if (!match) return;

    for (const [key, libValue] of Object.entries(match)) {
      if (NON_COPYABLE_LIB_FIELDS.has(key)) continue;
      if (libValue === undefined || libValue === null) continue;
      handleFieldUpdate(row.index, key, libValue);
    }
  };

  return (
    <EditableSelectCell
      value={row.original[field] ?? ""}
      options={options}
      disabled={options.length === 0}
      onChange={handleChange}
    />
  );
}

/* ------------------------------ Font styling ------------------------------ */

const addFontSize = (colArray) =>
  colArray.map((col) => ({
    ...col,
    muiTableHeadCellProps: {
      sx: { fontSize: FONT_SIZE },
      ...(col.muiTableHeadCellProps || {}),
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: FONT_SIZE,
        minWidth: col.size ? `${col.size}px` : "auto",
        maxWidth: col.size ? `${col.size * 2}px` : "auto",
        padding: "2px",
        textAlign: "center",
      },
      ...(col.muiTableBodyCellProps || {}),
    },
  }));

/* ------------------------------ Bulk columns ------------------------------ */

const commonCols = (
  allTables,
  SelectedParts,
  handleFieldUpdate,
  handleDeleteRow,
  isMetric = false,
  uidOptions,
  uidRows,
  specLibraryTable,
  isBulkTab = true,
) => {
  const libRows = buildLibRows(specLibraryTable, isBulkTab);

  const cascadeCell = (field) => ({ row }) => (
    <CascadeSelectCell
      row={row}
      field={field}
      libRows={libRows}
      handleFieldUpdate={handleFieldUpdate}
      isMetric={isMetric}
    />
  );

  const cols = [
    {
      accessorKey: "UID",
      header: "UID",
      size: 120,
      enableColumnActions: false,
      Cell: ({ cell, row }) => {
        const storedValue = cell.getValue() ?? "";
        const autoValue =
          storedValue ||
          matchUidByMaterialDescription(
            row.original?.["Material Description"] ?? row.original?.MaterialDescription,
            uidRows
          );

        // If value contains multiple UIDs (concatenated from P&ID), show as textbox
        const isMultiValue =
          String(autoValue).includes(",") || String(autoValue).includes("\n");

        if (isMultiValue) {
          return (
            <TextCommitCell
              value={autoValue}
              onCommit={(v) => handleFieldUpdate(row.index, "UID", v)}
            />
          );
        }

        return (
          <EditableSelectCell
            value={autoValue}
            options={uidOptions}
            onChange={(v) => handleFieldUpdate(row.index, "UID", v)}
          />
        );
      },
    },
    {
      accessorKey: "Spec",
      header: "Spec",
      enableColumnActions: false,
      size: 40,
      Cell: cascadeCell("Spec"),
    },
    {
      accessorKey: "Line Number",
      header: "Line Number",
      enableColumnActions: false,
      size: 20,
      Cell: ({ row }) => (
        <TextCommitCell
          value={row.original.Line}
          onCommit={(v) => handleFieldUpdate(row.index, "Line", v)}
        />
      ),
    },
    {
      accessorKey: "Components",
      header: "Components",
      enableColumnActions: false,
      size: 120,
      Cell: cascadeCell("Components"),
    },
    {
      accessorKey: "Size",
      header: "Size",
      enableColumnActions: false,
      size: 75,
      Cell: ({ row }) => (
        <Box
          sx={{
            backgroundColor: inputCellBg(row.original.MatchLevel, "Size"),
            width: "100%",
            px: 0.5,
          }}
        >
          <CascadeSelectCell
            row={row}
            field="Size"
            libRows={libRows}
            handleFieldUpdate={handleFieldUpdate}
            isMetric={isMetric}
          />
        </Box>
      ),
    },
    {
      accessorKey: "Schedule/Rating",
      header: "Schedule/Rating",
      enableColumnActions: false,
      size: 75,
      Cell: ({ row }) => (
        <Box
          sx={{
            backgroundColor: inputCellBg(
              row.original.MatchLevel,
              "Schedule/Rating",
            ),
            width: "100%",
            px: 0.5,
          }}
        >
          <CascadeSelectCell
            row={row}
            field="Schedule/Rating"
            libRows={libRows}
            handleFieldUpdate={handleFieldUpdate}
            isMetric={isMetric}
          />
        </Box>
      ),
    },
    {
      accessorKey: "Ends",
      header: "Ends",
      enableColumnActions: false,
      size: 40,
      Cell: ({ row }) => (
        <Box
          sx={{
            backgroundColor: inputCellBg(row.original.MatchLevel, "Ends"),
            width: "100%",
            px: 0.5,
          }}
        >
          <CascadeSelectCell
            row={row}
            field="Ends"
            libRows={libRows}
            handleFieldUpdate={handleFieldUpdate}
            isMetric={isMetric}
          />
        </Box>
      ),
    },
    {
      accessorKey: "Material",
      header: "Material",
      enableColumnActions: false,
      size: 80,
      Cell: cascadeCell("Material"),
    },
    {
      accessorKey: "Material Description",
      header: "Material Description",
      enableColumnActions: false,
      size: 180,
    },
    // Qty
    {
      accessorKey: "Qty",
      header: "Qty/Length",
      Header: () => (
        <div style={{ whiteSpace: "normal", textAlign: "center" }}>
          Qty/
          <br />
          Length
        </div>
      ),
      size: 20,
      enableColumnActions: false,
      Cell: ({ row, cell }) => (
        <NumberCommitCell
          value={row.original.Qty}
          placeholder="Enter Qty"
          onCommit={(v) => handleFieldUpdate(row.index, cell.column.id, v)}
        />
      ),
    },
    // Unit
    {
      accessorKey: "Unit",
      header: "Unit",
      enableColumnActions: false,
      size: 40,
      Cell: ({ row, cell }) => (
        <SelectCommitCell
          value={row.original.Unit}
          options={["ft", "ft²", "m", "m²", "Nos"]}
          onCommit={(v) => handleFieldUpdate(row.index, cell.column.id, v)}
        />
      ),
    },
    // Remarks
    {
      accessorKey: "Remarks",
      header: "Remarks",
      enableColumnActions: false,
      size: 100,
      Cell: ({ row }) => (
        <TextCommitCell
          value={row.original.Remarks}
          onCommit={(v) => handleFieldUpdate(row.index, "Remarks", v)}
        />
      ),
    },
    // ── Unit Weight — DB stores kg/unit, convert to lbs/unit when Imperial ───
    {
      accessorKey: "Weight",
      header: isMetric ? "Unit Weight (kg/unit)" : "Unit Weight (lbs/unit)",
      Header: () => (
        <div style={{ whiteSpace: "normal", textAlign: "center" }}>
          Unit Weight
          <br />
          {isMetric ? "(kg/unit)" : "(lbs/unit)"}
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 60,
      Cell: ({ row }) => {
        const weight = row.original.Weight;
        if (weight === "N/A") return "N/A";
        if (weight === undefined || weight === null || weight === "") return "";
        const num = Number(weight);
        if (!Number.isFinite(num)) return "";
        return num.toFixed(2);
      },
    },

    // Hours
    {
      accessorKey: "WeldHour",
      header: "Weld Hour (Hrs)",
      Header: () => (
        <div style={{ whiteSpace: "normal", textAlign: "center" }}>
          Weld Hour 
          <br />
         (Hrs)
        </div>
      ),
      enableColumnActions: false,
      size: 65,
    },
    {
      accessorKey: "BoltupHour",
      header: "Boltup Hour (Hrs)",
      Header: () => (
        <div style={{ whiteSpace: "normal", textAlign: "center" }}>
          Boltup Hour (Hrs)
          <br />
          
        </div>
      ),
      enableColumnActions: false,
      size: 65,
    },
    // ── Total Weight — DB stores kg, convert to lbs when Imperial ─────────────
    {
      accessorKey: "TotalWeight",
      header: isMetric ? "Total Weight (kg)" : "Total Weight (lbs)",
      Header: () => (
        <div style={{ whiteSpace: "normal", textAlign: "center" }}>
          Total Weight
          <br />
          {isMetric ? "(kg)" : "(lbs)"}
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 70,
      Cell: ({ row }) => {
        const totalWeight = row.original.TotalWeight;
        if (totalWeight === "N/A") return "N/A";
        if (
          totalWeight === undefined ||
          totalWeight === null ||
          totalWeight === ""
        )
          return "";
        const num = Number(totalWeight);
        if (!Number.isFinite(num)) return "";
        return num.toFixed(2);
      },
    },
    {
      accessorKey: "TotalFabHours",
      header: "Total Fab Hours (Hrs)",
      Header: () => (
        <div style={{ whiteSpace: "normal", textAlign: "center" }}>
          Total Fab Hours (Hrs)
          <br />
          
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 75,
      Cell: ({ row }) => {
        const tfh = row.original?.TotalFabHours;
        if (tfh === "N/A") return "N/A";
        const tfhNum = Number(tfh);
        if (Number.isFinite(tfhNum)) return tfhNum.toFixed(2);
        return "";
      },
    },
    {
      id: "Actions",
      header: "Actions",
      enableColumnActions: false,
      size: 40,
      Cell: ({ row }) => (
        <DeleteRowCell onDelete={() => handleDeleteRow(row.index)} />
      ),
    },
  ];

  return addFontSize(cols);
};

/* ------------------------------ Tagged columns ----------------------------- */

const colsWithTagNumbers = (
  allTables,
  SelectedParts,
  handleFieldUpdate,
  handleDeleteRow,
  isMetric = false,
  dataSheetsStore,
  uidOptions,
  uidRows,
  specLibraryTable,
 ) => {
  const cols = commonCols(
    allTables,
    SelectedParts,
    handleFieldUpdate,
    handleDeleteRow,
    isMetric,
   uidOptions,
   uidRows,
   specLibraryTable,
   false, // isBulkTab — Tag Materials holds Valve/Instrument rows
  );

  // const tagCol = {
  //   accessorKey: "TagNumber",
  //   header: "Tag Number",
  //   enableColumnActions: false,
  //   size: 100,
  //   Cell: ({ row }) => (
  //     <TextCommitCell
  //       value={row.original.TagNumber}
  //       onCommit={(v) => handleFieldUpdate(row.index, "TagNumber", v)}
  //     />
  //   ),
  // };
  const tagNumberCol = {
    accessorKey: "TagNumber",
    header: "Tag Number",
    size: 120,
    enableColumnActions: false,
    Cell: ({ row }) => (
          <TextCommitCell
            value={row.original.TagNumber}
            onCommit={(v) => handleFieldUpdate(row.index, "TagNumber", v)}
          />

        ),

  };



  const dataSheetCol = {
    accessorKey: "DataSheet",
    header: "Data Sheet",
    size: 60,
    enableColumnActions: false,
    enableSorting: false,
    Cell: ({ row }) => (
      <DataSheetCell row={row} dataSheetsStore={dataSheetsStore} />
    ),
  };

  // return addFontSize([tagNumberCol, ...cols.slice(0, 2), dataSheetCol, ...cols.slice(2)]

  return addFontSize([cols[0], tagNumberCol, ...cols.slice(1), dataSheetCol]);
  
  //   ...cols.slice(0, 2),
  //   tagCol,
  //   dataSheetCol,
  //   ...cols.slice(2),
  // ]);
};

/* ------------------------------ Exported map ------------------------------ */

/** @type {MRT_ColumnDef<any>[]} */
export const columns = (
  allTables,
  SelectedParts,
  handleFieldUpdate,
  handleDeleteRow,
  isMetric = false,
  dataSheetsStore,
  uidOptions = [],
  uidRows = [],
  specLibraryTable = [],
) => ({
  "Bulk Materials": commonCols(
    allTables,
    SelectedParts,
    handleFieldUpdate,
    handleDeleteRow,
    isMetric,
    uidOptions,
    uidRows,
    specLibraryTable,
    true, // isBulkTab
  ),

  "Tag Materials": colsWithTagNumbers(
    allTables,
    SelectedParts,
    handleFieldUpdate,
    handleDeleteRow,
    isMetric,
    dataSheetsStore,
    uidOptions,
    uidRows,
    specLibraryTable,
  ),

  BOM: [
    {
      accessorKey: "ItemNo",
      header: "Item No",
      enableColumnActions: false,
      size: 50,
    },
    {
      accessorKey: "MatDesc",
      header: "Material Description",
      enableColumnActions: false,
      size: 150,
    },
    {
      accessorKey: "Quantity",
      header: "Qty",
      enableColumnActions: false,
      size: 50,
    },
    {
      accessorKey: "dimension",
      header: "Dimension",
      enableColumnActions: false,
      size: 50,
    },
    {
      accessorKey: "ItemUnit",
      header: "Unit",
      enableColumnActions: false,
      size: 50,
    },
    // ── BOM Weight — stored in kg, convert to lbs when Imperial ───────────────
    {
      accessorKey: "totWeight",
      header: isMetric ? "Weight (kg)" : "Weight (lb)",
      enableColumnActions: false,
      size: 50,
      Cell: ({ row }) => {
        const w = row.original.totWeight;
        if (w === "-") return "-";
        const num = Number(w) || 0;
        return num ? num.toFixed(2) : "0";
      },
    },
  ],
});

/* ------------------------------ Summary columns ----------------------------- */

export const summaryColumns = (onFieldUpdate) =>
  addFontSize([
    {
      accessorKey: "wbsCode",
      header: "WBS Code",
      enableColumnActions: false,
      size: 160,
    },
    {
      accessorKey: "taskDescription",
      header: "Task Description",
      enableColumnActions: false,
      size: 200,
    },
    {
      accessorKey: "unit",
      header: "Unit",
      enableColumnActions: false,
      size: 80,
      Cell: ({ row }) =>
        row.original.kind === "computed" ? (
          row.original.unit
        ) : (
          <TextCommitCell
            value={row.original.unit}
            onCommit={(v) => onFieldUpdate(row.original, "unit", v)}
          />
        ),
    },
    {
      accessorKey: "estUnit",
      header: "Est Unit",
      enableColumnActions: false,
      size: 100,
      Cell: ({ row }) => {
        if (row.original.kind === "computed") {
          const n = Number(row.original.estUnit);
          return Number.isFinite(n) ? n.toFixed(2) : "";
        }
        return (
          <NumberCommitCell
            value={row.original.estUnit}
            placeholder="Enter qty"
            onCommit={(v) => onFieldUpdate(row.original, "estUnit", v)}
          />
        );
      },
    },
    {
      accessorKey: "estUnitRate",
      header: "Est Unit Rate ($)",
      enableColumnActions: false,
      size: 100,
      Cell: ({ row }) => (
        <NumberCommitCell
          value={row.original.estUnitRate}
          placeholder="Enter rate"
          onCommit={(v) => onFieldUpdate(row.original, "estUnitRate", v)}
        />
      ),
    },
    {
      accessorKey: "estCost",
      header: "Est Cost ($)",
      enableColumnActions: false,
      size: 100,
      Cell: ({ row }) => {
        const rate = row.original.estUnitRate;
        if (rate === "" || rate === undefined || rate === null) return "";
        const unit = Number(row.original.estUnit) || 0;
        const rateNum = Number(rate);
        if (!Number.isFinite(rateNum)) return "";
        return (unit * rateNum).toFixed(2);
      },
    },
  ]);

export const tabLabels = ["Bulk Materials", "Tag Materials", "Summary", "BOM"];
